<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { SearchFilter } from '@client/components/filters/search';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import Pusher from '@client/pages/signatures/pusher/components/pusher.svelte';

  import { PushHistoryService } from '@client/services/push-history';

  import { pusherStore } from '@client/stores/pusher';

  import { PushHistoryList } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let pushed = false;
  let pusherConfig = {};
  let pushTo = null;
  let userCount = 0;
  let pushHistory = [];
  let filteredHistory = [];
  let selectedPushId = null;
  let selectedPush = null;
  let search = '';

  const loadPushHistory = async () => {
    try {
      const response = await PushHistoryService.get();
      pushHistory = Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Failed to load push history:', error);
      notify($format('label.FAILURE.LOAD_PUSH_HISTORY'), 'error');
    }
  };

  const selectPush = (pushId) => {
    selectedPushId = pushId;
    selectedPush = pushHistory.find((entry) => entry._id === pushId);
  };

  const handlePushSelect = () => {
    if (selectedPush) {
      pusherConfig = { ...selectedPush };
      currentInternalStep = 1;
      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const executePush = async () => {
    if (!pushed) {
      throw new Error($format('label.PUSH_NOT_COMPLETED'));
    }

    step.data = {
      id: pushTo || 'unknown',
      pushType: pusherConfig.mode?.value || pusherConfig.note?.pushMode || 'signature'
    };

    notify($format('label.SUCCESS.PUSH_COMPLETED'));
    return { success: true };
  };

  onMount(async () => {
    loading = true;
    try {
      await loadPushHistory();
    } catch (error) {
      console.error('Failed to initialize repeat push form:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executePush);
    }
  });

  $: if (pusherConfig.csvConfig?.sigdata?.data) {
    userCount = pusherConfig.csvConfig.sigdata.data.length;
  }

  $: {
    if (currentInternalStep === 0) {
      nextDisabled = !selectedPush || loading;
    } else if (currentInternalStep === 1) {
      nextDisabled = !pushed;
    }
  }

  // Filter push history based on search
  $: filteredHistory = search?.length
    ? pushHistory.filter((entry) => {
        const searchValue = search.toLowerCase();
        return [...Object.values(entry), ...Object.values(entry?.note || {})].some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(searchValue);
          }
          return false;
        });
      })
    : pushHistory;

  $: selectedPushSummary = selectedPush
    ? `${selectedPush.action || 'Push'} - ${new Date(selectedPush.timestamp).toLocaleDateString()}`
    : 'Not selected';
</script>

<div class="pusher-repeat-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.SELECT_PREVIOUS_PUSH')}
    summary={selectedPushSummary}
    onNext={currentInternalStep === 0 ? handlePushSelect : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={!selectedPush || loading}
  >
    <div class="search-section">
      <SearchFilter bind:search />
    </div>

    <div class="history-list">
      <PushHistoryList history={filteredHistory} bind:selectedPushId onSelectPush={selectPush} {loading} />
    </div>
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title="Configure & Push"
    summary={pushed ? $format('label.PUSH_COMPLETED') : $format('label.NOT_COMPLETED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={!pushed}
  >
    {#if pusherConfig && Object.keys(pusherConfig).length > 0}
      <Pusher config={pusherConfig} bind:pushed bind:pushTo workflowMode={true} />
    {/if}
  </Step>
</div>

<style lang="scss">
  .pusher-repeat-form {
    .search-section {
      margin-bottom: var(--margin-large);
    }

    .history-list {
      margin-top: var(--margin-base);
    }
  }
</style>
